import { defineStore } from 'pinia';
import { ref } from 'vue';
import { GetJobRequestById } from '../services/JobRequests/GetJobRequestById';
import { GetCompanyDepartments } from '../services/Companies/GetCompanyDepartments';
import { GetJobRequisitionsApprovers } from '../services/JobRequests/GetJobRequestionApprovers';
import { GetJobRequestOrganizations } from '../services/JobRequests/GetJobRequestOrganizations';
import { PostJobRequestForm } from '../services/JobRequests/PostJobRequest';
import { useRouter } from 'vue-router';
import { PostJobRequestApprovals } from '../services/JobRequests/PostJobRequestApprovals';
import { PostJobRequestSynchronization } from '../services/JobRequests/PostJobRequestSynchronization';
import { GetApproverStatus } from '../services/JobRequests/GetApproverStatus';
import { GetUserType } from '../services/JobRequests/GetUserType';
import { GetAllJobRequestByStatus } from '../services/JobRequests/GetAllJobRequestByStatus';

export const useJobRequestStore = defineStore('jobRequest', () => {
  const isConnected = ref(true);
  const cardDrawerState = ref(false);
  const jobRequestEditDrawer = ref(false);
  const isFeatureFlagOn = ref(false);
  const defaultJobRequestList = ref([]);
  const selectedJobRequestData = ref([]);
  const animateClick = ref(false);
  const activeForm = ref(1);
  const departmentList = ref([]);
  const jobRequisitionsApproverList = ref([]);
  const organizationList = ref([]);
  const showOrganizationModal = ref(false);
  const showEmptyApprovelistModal = ref(false);
  const successCreateFormToast = ref(false);
  const errorCreateFormToast = ref(false);
  const router = useRouter();
  const isListLoading = ref(false);

  const isUserApprover = ref(false);

  const showResubmitModal = ref(false);
  const actionType = ref('');
  const showRejectModal = ref(false);

  const showErrorJobRequestToast = ref(false);
  const jobRequestToastErrorMessage = ref('');
  const showSuccessJobRequestToast = ref(false);
  const jobRequestToastSuccessMessage = ref('Approved');
  const isUserAdmin = ref(false);
  const showBannerSyncForManatal = ref(true);
  const infoSyncToManatalToast = ref(false);
  const unsyncedCounter = ref(1);

  const userType = ref('Admin');
  const approvalProgressData = ref();
  const forApprovalJobRequestData = ref([]);
  const pendingJobRequestData = ref([]);
  const approvedJobRequestData = ref([]);
  const rejectedJobRequestData = ref([]);
  const isRejectedBoardScrolling = ref(false);
  const isApprovedBoardScrolling = ref(false);
  const isPendingBoardScrolling = ref(false);
  const isWaitingForApprovalScrolling = ref(false);
  const isApprovalDataLoading = ref(true);
  const isFromColumn = ref('');

  const createJobRequestForm = ref({
    requestFor: {
      jobTitle: '',
      organizationId: '',
      departmentId: '',
      targetFillDate: '',
      priorityLevelId: '',
      headCount: '',
      reasonId: '',
      reasonNotes: '',
    },
    jobSetup: {
      employmentTypeId: '',
      expLevelId: '',
      workSetupId: '',
      location: '',
      scheduleTypeId: '',
    },
    jobDescription: '',
    jobRequirements: '',
    salary: {
      currency: '',
      minSalary: '',
      maxSalary: '',
      payrollType: '',
    },
    benefits: {
      hmo: false,
      lifeInsurance: false,
      commission: false,
      health: false,
      accidentalInsurance: false,
      others: false,
    },
    benefitsDescription: '',
  });

  const requestFor = ref({
    jobTitle: {
      hasError: false,
      errorMessage: '',
    },
    organizationId: {
      hasError: false,
      errorMessage: '',
    },
    departmentId: {
      hasError: false,
      errorMessage: '',
    },
    headCount: {
      hasError: false,
      errorMessage: '',
    },
    targetFillDate: {
      hasError: false,
      errorMessage: '',
    },
  });

  const jobSetup = ref({
    jobTitle: {
      hasError: false,
      errorMessage: '',
    },
    organizationId: {
      hasError: false,
      errorMessage: '',
    },
    departmentId: {
      hasError: false,
      errorMessage: '',
    },
    employmentTypeId: {
      hasError: false,
      errorMessage: '',
    },
    expLevelId: {
      hasError: false,
      errorMessage: '',
    },
    location: {
      hasError: false,
      errorMessage: '',
    },
    workSetup: {
      hasError: false,
      errorMessage: '',
    },
    minSalary: {
      hasError: false,
    },
    maxSalary: {
      hasError: false,
    },
    benefitType:{
      hasError: true,
      errorMessage: '',
    },
    benefitDetails: {
      hasError:false,
      errorMessage: '',
    },
  });

  const jobDescriptionAndRequirements = ref({
    jobDescription: {
      hasError: false,
      errorMessage: '',
    },
    jobRequirements: {
      hasError: false,
      errorMessage: '',
    },
  });

  const resetFormState = () => {
    const requestFor = createJobRequestForm.value.requestFor;
    const jobSetup = createJobRequestForm.value.jobSetup;
    const salary = createJobRequestForm.value.salary;
    const benefits = createJobRequestForm.value.benefits;

    requestFor.jobTitle = '';
    requestFor.organizationId = '';
    requestFor.departmentId = '';
    requestFor.targetFillDate = '';
    requestFor.headCount = '';
    requestFor.priorityLevelId = '';
    requestFor.reasonId = '';
    requestFor.reasonNotes = '';

    jobSetup.employmentTypeId = '';
    jobSetup.expLevelId = '';
    jobSetup.workSetupId = '';
    jobSetup.location = '';
    jobSetup.scheduleTypeId = '';

    createJobRequestForm.value.jobDescription = '';
    createJobRequestForm.value.jobRequirements = '';

    salary.currency = '';
    salary.minSalary = '';
    salary.maxSalary = '';
    salary.payrollType = '';

    benefits.hmo = false;
    benefits.lifeInsurance = false;
    benefits.commission = false;
    benefits.health = false;
    benefits.accidentInsurance = false;
    benefits.others = false;

    createJobRequestForm.value.benefitsDescription = '';
  };

  const validateInput = (input, regex) => {
    if (input === '') {
      return {
        hasError: true,
        errorMessage: 'This field is required.',
      };
    } else if (regex && !regex.test(input)) {
      return {
        hasError: true,
        errorMessage:
          'This field does not allow special characters. (e.g. ! @ # $ % ^ & * )',
      };
    } else if (input <= 0) {
      return {
        hasError: true,
        errorMessage: 'The input value should be greater than 0.',
      };
    } else {
      return {
        hasError: false,
        errorMessage: '',
      };
    }
  };

  const priorityLevelList = ref([
    { text: 'High', value: 1 },
    { text: 'Medium', value: 2 },
    { text: 'Low', value: 3 },
  ]);

  const reasonList = ref([
    { text: 'New Position', value: 1 },
    { text: 'Replacement', value: 2 },
  ]);

  const scheduleTypeList = ref([
    { text: 'Day Shift', value: 1 },
    { text: 'Night Shift', value: 2 },
    { text: 'Swing Shift  ', value: 3 },
    { text: 'Rotating Shift ', value: 4 },
    { text: 'Weekend Shift  ', value: 5 },
    { text: 'On Call Shift   ', value: 6 },
  ]);

  const employmentTypeList = ref([
    { text: 'Full Time', value: 1 },
    { text: 'Part Time', value: 2 },
    { text: 'Temporary', value: 3 },
    { text: 'Freelance', value: 4 },
    { text: 'Internship ', value: 5 },
    { text: 'Apprenticeship  ', value: 6 },
    { text: 'Contractor ', value: 7 },
    { text: 'Consultancy ', value: 8 },
  ]);

  const expLevelList = ref([
    { text: 'Intern', value: 6 },
    { text: 'Entry', value: 5 },
    { text: 'Associate', value: 4 },
    { text: 'Mid-Senior', value: 3 },
    { text: 'Director', value: 2 },
    { text: 'Executive', value: 1 },
  ]);

  const workSetupList = ref([
    { text: 'On Site', value: 3 },
    { text: 'Hybrid', value: 2 },
    { text: 'Work From Home', value: 1 },
  ]);

  const payrollTypeList = ref([
    { text: 'Hourly', value: 1 },
    { text: 'Daily', value: 2 },
    { text: 'Weekly', value: 3 },
    { text: 'Semi-Monthly', value: 6 },
    { text: 'Monthly', value: 4 },
    { text: 'Yearly', value: 5 },
  ]);

  const benefitTypeList = ref([
    { text: 'HMO after 6 mos', value: '1', name: 'hmo' },
    { text: 'Life Insurance', value: '2', name: 'lifeInsurance' },
    { text: 'Commission', value: '3', name: 'commission' },
    { text: 'Health', value: '4', name: 'health' },
    { text: 'Accidental Insurance', value: '5', name: 'accidentalInsurance' },
    { text: 'Other', value: '6', name: 'others' },
  ]);

  const currencyList = ref([{ text: 'PHP', value: 'PHP' }]);

  const getJobRequestById = async (jobRequestId) => {
    const response = await GetJobRequestById(jobRequestId);
    selectedJobRequestData.value = response;
  };

  const getJobRequisitionApprovers = async (departmendId) => {
    await GetJobRequisitionsApprovers(departmendId)
      .then((response) => {
        jobRequisitionsApproverList.value = response;
        showEmptyApprovelistModal.value = false;
      })
      .catch(() => {
        showEmptyApprovelistModal.value = true;
      });
  };

  const getJobRequestOrganizations = async () => {
    try {
      const response = await GetJobRequestOrganizations();
      processOrganizationDropdownList(response.result);
    } catch (error) {
      showOrganizationModal.value = true;
      return;
    }
  };

  const processOrganizationDropdownList = (orgList) => {
    const optionsList = orgList.map((value) => {
      return {
        ...value,
        text: value.name,
        value: value.id,
      };
    });
    organizationList.value = optionsList;
  };

  const departmentListPage = ref({
    currentPage: 1,
    totalPages: 0,
  });
  const companyIdHolder = ref('');
  const departmentListLoader = ref(false);

  const getCompanyDepartments = async (companyId, pageNumber) => {
    departmentListLoader.value = true;
    companyIdHolder.value = companyId;
    const data = await GetCompanyDepartments({
      CompanyId: companyId,
      PageNumber: pageNumber,
      RowsPerPage: 100,
    });
    departmentList.value = departmentList.value.concat(data.data);
    departmentListLoader.value = false;
    departmentListPage.value = {
      ...departmentListPage.value,
      currentPage: data.pagination.currentPage,
    };
    departmentListPage.value = {
      ...departmentListPage.value,
      totalPages: data.pagination.totalPages,
    };
    processDepartmentDropdownList(departmentList.value);
  };

  const processDepartmentDropdownList = (depList) => {
    const optionsList = depList.map((val) => {
      return {
        ...val,
        text: val.departmentName,
        value: val.id,
      };
    });
    departmentList.value = optionsList;
  };

  const postJobRequest = async (formData) => {
    try {
      await PostJobRequestForm(formData);
      successCreateFormToast.value = true;
      setTimeout(() => {
        resetFormState();
        activeForm.value = 1;
        router.push({ name: 'job-request' });
      }, 1000);
    } catch (error) {
      errorCreateFormToast.value = true;
    }
  };

  const updateAllColumns = () => {
    getAllJobRequestByStatus('ForApproval');
    getAllJobRequestByStatus('Pending');
    getAllJobRequestByStatus('Approved');
    getAllJobRequestByStatus('Rejected');
  };

  const postJobRequestApprovals = async (params) => {
    try {
      await PostJobRequestApprovals(params);
      if (params.approvalActionType === 3 || params.approvalActionType === 2) {
        showResubmitModal.value = false;
        showRejectModal.value = false;
        cardDrawerState.value = false;
        showSuccessJobRequestToast.value = false;
        updateAllColumns();
        cardDrawerState.value = false;
      } else {
        showSuccessJobRequestToast.value = true;
        cardDrawerState.value = false;
        updateAllColumns();
      }
    } catch (error) {
      showErrorJobRequestToast.value = true;
      jobRequestToastErrorMessage.value = error.response.data.message;
      cardDrawerState.value = false;
      showRejectModal.value = false;
      showResubmitModal.value = false;
    }
  };

  const postJobRequestSynchronization = async () => {
    try {
      await PostJobRequestSynchronization();
      infoSyncToManatalToast.value = true;
    } catch (error) {
      console.log(error);
    }
  };

  const getUserType = async () => {
    try {
      const response = await GetUserType();
      userType.value = response.userType;
    } catch (error) {
      console.log(error);
    }
  };

  const getApproverStatus = async (jobRequestId) => {
    try {
      isApprovalDataLoading.value = true;
      const response = await GetApproverStatus(jobRequestId);
      approvalProgressData.value = response;
      isApprovalDataLoading.value = false;
    } catch (error) {
      console.log(error);
    }
  };

  const getAllJobRequestByStatus = async (status) => {
    try {
      const response = await GetAllJobRequestByStatus(status);
      switch (status) {
        case 'ForApproval':
          defaultJobRequestList.value = response;
          forApprovalJobRequestData.value = response;
          break;
        case 'Pending':
          pendingJobRequestData.value = response;
          break;
        case 'Approved':
          approvedJobRequestData.value = response;
          break;
        case 'Rejected':
          rejectedJobRequestData.value = response;
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(error);
    }
  };

  return {
    isConnected,
    cardDrawerState,
    jobRequestEditDrawer,
    userType,
    isFeatureFlagOn,
    priorityLevelList,
    defaultJobRequestList,
    scheduleTypeList,
    getJobRequestById,
    selectedJobRequestData,
    reasonList,
    employmentTypeList,
    expLevelList,
    workSetupList,
    payrollTypeList,
    benefitTypeList,
    currencyList,
    activeForm,
    animateClick,
    createJobRequestForm,
    validateInput,
    requestFor,
    jobSetup,
    departmentList,
    jobRequisitionsApproverList,
    getJobRequisitionApprovers,
    getJobRequestOrganizations,
    organizationList,
    showOrganizationModal,
    showEmptyApprovelistModal,
    postJobRequest,
    resetFormState,
    successCreateFormToast,
    isListLoading,
    getCompanyDepartments,
    departmentListPage,
    companyIdHolder,
    departmentListLoader,
    isUserApprover,
    showResubmitModal,
    showRejectModal,
    postJobRequestApprovals,
    actionType,
    jobRequestToastErrorMessage,
    jobRequestToastSuccessMessage,
    showSuccessJobRequestToast,
    showErrorJobRequestToast,
    errorCreateFormToast,
    isUserAdmin,
    postJobRequestSynchronization,
    showBannerSyncForManatal,
    getApproverStatus,
    approvalProgressData,
    infoSyncToManatalToast,
    unsyncedCounter,
    getUserType,
    forApprovalJobRequestData,
    pendingJobRequestData,
    approvedJobRequestData,
    rejectedJobRequestData,
    getAllJobRequestByStatus,
    isRejectedBoardScrolling,
    isApprovedBoardScrolling,
    isPendingBoardScrolling,
    isWaitingForApprovalScrolling,
    isApprovalDataLoading,
    updateAllColumns,
    isFromColumn,
    jobDescriptionAndRequirements,
  };
});
